import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useTheme } from '../../providers/ThemeProvider';
import { LanguageSelector } from './LanguageSelector';
import { NavMenu } from './NavMenu';
import Styles from './SharedHeader.module.css';
import { Button, Image, Label, useAuth } from '@app/engine';
import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/** Tab definition for the About header. */
interface IAboutTab {
  key: string;
  label: string;
  route: string;
}

/** Props for the shared header used by both Landing and About pages. */
interface ISharedHeaderProps {
  homeStyle: boolean;
  onLogo: () => void;
  onGetStarted: () => void;
  onLogin?: () => void;
  onDemo?: () => void;
}

/**
 * Shared sticky header for the Landing and About pages.
 *
 * When `homeStyle` is true, renders the landing header with language selector, demo link and login link. When `homeStyle` is false, renders the about header with tab navigation (Demo | Company |
 * Terms | Help).
 *
 * @param props - Component props.
 * @returns The header element.
 */
export function SharedHeader(props: ISharedHeaderProps) {
  const { homeStyle, onLogo, onGetStarted, onLogin, onDemo } = props;
  const { isAuthenticated } = useAuth();
  const { getThemedAssetDTO } = useTheme();
  const location = useLocation();

  const logoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'Logo.png'), [getThemedAssetDTO]);
  const loginText = isAuthenticated ? Localized.LandingNavDashboard : Localized.LandingNavLogin;
  const activeTab = useMemo(() => location.pathname.split('/').pop() ?? 'demo', [location.pathname]);

  const aboutTabs: IAboutTab[] = [
    { key: 'demo', label: Localized.AboutNavDemo, route: Constants.Route.AboutDemo },
    { key: 'company', label: Localized.AboutNavCompany, route: Constants.Route.AboutCompany },
    { key: 'terms', label: Localized.AboutNavTerms, route: Constants.Route.AboutTerms },
    { key: 'help', label: Localized.AboutNavHelp, route: Constants.Route.AboutHelp }
  ];

  const landingLinks = [...(onDemo ? [{ label: Localized.AboutNavDemo, onClick: onDemo }] : []), ...(onLogin ? [{ label: loginText, onClick: onLogin }] : [])];

  const aboutMenuItems = aboutTabs.filter((t) => t.key !== activeTab).map((t) => ({ label: t.label, href: t.route }));

  return (
    <header className={`${Styles.header} ${homeStyle ? Styles.headerAbsolute : Styles.headerFixed}`}>
      <div className={Styles.headerInner}>
        <div className={Styles.headerLeft}>
          <Image title={Localized.LandingLogoAlt} assetDTO={logoAssetDTO} classNameContainer={Styles.logoContainer} classNameImageEnabled={Styles.logoImg} onClickHandler={onLogo} />
        </div>
        <nav className={Styles.nav}>
          <div className={Styles.navTopRow}>
            <Button text={Localized.SharedGetStarted} classNameButtonEnabled={Styles.getStartedBtn} onClickHandler={onGetStarted} />
          </div>
          {homeStyle ? (
            <div className={Styles.navMeta}>
              <LanguageSelector classNameTrigger={Styles.navPill} />
              <div className={Styles.navMetaLandscape}>
                {landingLinks.map((item, position) => (
                  <Label key={position} text={item.label} classNameContainer={Styles.navPill} onClickHandler={item.onClick} />
                ))}
              </div>
              {landingLinks.length > 0 && (
                <div className={Styles.navMetaPortrait}>
                  <NavMenu triggerLabel="☰" items={landingLinks} classNameTrigger={Styles.navPill} />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className={Styles.tabRowLandscape}>
                {aboutTabs.map((t) => (
                  <NavLink key={t.key} to={t.route} className={({ isActive }) => `${Styles.navPill} ${Styles.tabLink} ${isActive ? Styles.tabLinkActive : ''}`}>
                    {t.label}
                  </NavLink>
                ))}
              </div>
              <div className={Styles.tabRowPortrait}>
                <Button type="button" text={aboutTabs.find((t) => t.key === activeTab)?.label ?? activeTab} classNameButtonEnabled={`${Styles.navPill} ${Styles.tabLink} ${Styles.tabLinkActive}`} />
                <NavMenu triggerLabel="☰" items={aboutMenuItems} classNameTrigger={Styles.navPill} />
              </div>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
